import { useEffect } from "react";
import { useToast } from "../components/Toast";

/**
 * Handles the return leg of the Cloudreve authorisation.
 *
 * This lives at the dashboard level rather than inside the connection panel on
 * purpose: the operator comes back from Cloudreve straight into a fresh page
 * load, with the settings panel closed, so a handler mounted inside that panel
 * would never run.
 *
 * Two arrivals are possible and both are handled here: a popup, which reports
 * to its opener and closes itself, and a same-tab navigation, which is what
 * happens when the browser blocks the popup.
 */
export function useCloudreveCallback(onChanged: () => void): void {
  const { notify } = useToast();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get("cloudreve");
    if (!outcome) return;

    const detail = params.get("detail") ?? "";
    const connected = outcome === "connected";

    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ source: "cloudreve-uploader", connected, detail }, window.origin);
      window.close();
      return;
    }

    notify(
      detail || (connected ? "Connected to Cloudreve." : "Could not connect."),
      connected ? "success" : "error",
    );
    // Drop the parameters so a reload does not replay the message.
    window.history.replaceState({}, "", window.location.pathname);
    onChanged();
  }, [notify, onChanged]);

  // A popup reports its outcome here before closing.
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== window.origin) return;
      if (event.data?.source !== "cloudreve-uploader") return;

      const { connected, detail } = event.data;
      notify(
        detail || (connected ? "Connected to Cloudreve." : "Could not connect."),
        connected ? "success" : "error",
      );
      onChanged();
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [notify, onChanged]);
}
